/**
 * The "world content" tag (E4.6.S3 slice 2e, AD-705).
 *
 * A networked dungeon run is drawn at the instance's own frame — plan-local
 * coordinates round the origin, which overlap the open world's terrain — so
 * while a run is drawn the open world must not be. The roots that make up the
 * open world (the scene builder's world root and sky, placed NPCs and their
 * markers, gathering nodes and their rings, the techdemo entrance layer) carry
 * this tag on the TOP-LEVEL object they add to the scene; `dungeon/runScene.ts`
 * hides exactly the tagged top-level children while a run is drawn.
 *
 * Opt-in by design: anything untagged (entities, health bars, telegraphs, the
 * move marker, lights) keeps drawing, so a body or a telegraph born mid-run can
 * never vanish; the failure mode of a missed tag is a visible stray world prop,
 * never an invisible enemy. Presentation-only; frontend-local (AD-122).
 */

import type { Object3D } from 'three';

/** `userData` key marking a top-level scene child as open-world content. */
export const WORLD_CONTENT_KEY = 'worldContent';

/**
 * Tag `object` as open-world content; returns it for chaining. Every real three
 * object has `userData`; the placers' structural test doubles may not, and are
 * left untouched rather than grown a field their assertions do not expect.
 */
export function markWorldContent<T extends Object3D>(object: T): T {
  const data = object.userData as Record<string, unknown> | undefined;
  if (data) data[WORLD_CONTENT_KEY] = true;
  return object;
}

/** True when `object` carries the world-content tag. */
export function isWorldContent(object: Object3D): boolean {
  const data = object.userData as Record<string, unknown> | undefined;
  return data?.[WORLD_CONTENT_KEY] === true;
}
